import { PAGINATION } from '../../common/constant';

// Initial state for PIP module
export const initialPipState = {
    // Template state
    templates: [],
    currentTemplate: null,
    templateTotalRecords: 0,
    templateLoading: false,
    templateFilters: {
        currentPage: PAGINATION?.CURRENT_PAGE || 1,
        limit: PAGINATION?.LIMIT || 10,
        search: '',
        status: 1,
    },

    // Plan state (for future plan list pages)
    plans: [],
    currentPlan: null,
    planTotalRecords: 0,
    planLoading: false,
    planFilters: {
        currentPage: PAGINATION?.CURRENT_PAGE || 1,
        limit: PAGINATION?.LIMIT || 10,
        search: '',
        status: [],
        approvalStatus: [],
        decision: [],
        department: [],
        designation: [],
        startDate: null,
        endDate: null,
    },

    // Plan list — user-preferred visible columns (tabView).
    // null = preference not yet fetched from backend; consumers fall
    // back to PIP_PLAN_DEFAULT_VISIBLE_COLUMNS so the existing 14-column
    // layout keeps rendering without a flicker.
    planVisibleColumns: null,

    // Approval state — filters match what backend `performance|pip|approval|list` actually accepts
    // (approvalStatus/limit/currentPage/search). Additional client-side filters are NOT plumbed
    // until backend supports them; do not re-add inert filter fields here.
    approvals: [],
    approvalTotalRecords: 0,
    approvalLoading: false,
    approvalFilters: {
        currentPage: PAGINATION?.CURRENT_PAGE || 1,
        limit: PAGINATION?.LIMIT || 10,
        approvalStatus: 1,
        search: '',
        sort: null,
    },

    // Task Approval state: filters for `performance|pip|usertask|approvalList`.
    // Kept separate from `approvalFilters` so PIP Approval Request and Task
    // Approval Request screens don't cross-pollute pagination/search/status.
    taskApprovalFilters: {
        currentPage: PAGINATION?.CURRENT_PAGE || 1,
        limit: PAGINATION?.LIMIT || 10,
        approvalStatus: 1,
        user: null,
        sort: null,
    },

    // My PIP state (employee self-view — task-level list per USER_TASK_LIST)
    myPips: [],
    myPipTotalRecords: 0,
    myPipLoading: false,
    myPipFilters: {
        currentPage: PAGINATION?.CURRENT_PAGE || 1,
        limit: PAGINATION?.LIMIT || 10,
        search: '',
        status: [],
        pipId: null,
        startDate: null,
        endDate: null,
    },

    // Closed plans state
    closedPlans: [],
    closedTotalRecords: 0,
    closedLoading: false,
    closedFilters: {
        currentPage: PAGINATION?.CURRENT_PAGE || 1,
        limit: PAGINATION?.LIMIT || 10,
        department: [],
        designation: [],
        decision: [],
        startDate: null,
        endDate: null,
    },
};

// PIP reducer for handling PIP state changes
export const pipReducer = (state, action) => {
    switch (action.type) {
        // ---- Template Actions ----
        case 'PIP_SET_TEMPLATE_LOADING': {
            return { ...state, templateLoading: action.payload };
        }
        case 'PIP_SET_TEMPLATE_LIST': {
            return {
                ...state,
                templates: action.payload?.results || action.payload?.rows || [],
                templateTotalRecords: action.payload?.totalRecords || 0,
                templateLoading: false,
            };
        }
        case 'PIP_SET_CURRENT_TEMPLATE': {
            return { ...state, currentTemplate: action.payload };
        }
        case 'PIP_SET_TEMPLATE_FILTERS': {
            const newFilters = { ...state.templateFilters, ...action.payload };
            if (action.payload?.search !== undefined || action.payload?.status !== undefined) {
                if (action.payload?.currentPage === undefined) {
                    newFilters.currentPage = initialPipState.templateFilters.currentPage;
                }
            }
            return { ...state, templateFilters: newFilters };
        }

        // ---- Plan Actions ----
        case 'PIP_SET_PLAN_LOADING': {
            return { ...state, planLoading: action.payload };
        }
        case 'PIP_SET_PLAN_LIST': {
            return {
                ...state,
                plans: action.payload?.results || action.payload?.rows || [],
                planTotalRecords: action.payload?.totalRecords || 0,
                planLoading: false,
            };
        }
        case 'PIP_SET_CURRENT_PLAN': {
            return { ...state, currentPlan: action.payload };
        }
        case 'PIP_SET_PLAN_VISIBLE_COLUMNS': {
            return {
                ...state,
                planVisibleColumns: Array.isArray(action.payload) ? action.payload : [],
            };
        }
        case 'PIP_SET_PLAN_FILTERS': {
            const newFilters = { ...state.planFilters, ...action.payload };
            if (action.payload?.currentPage === undefined && (
                action.payload?.search !== undefined ||
                action.payload?.status !== undefined ||
                action.payload?.department !== undefined ||
                action.payload?.designation !== undefined
            )) {
                newFilters.currentPage = initialPipState.planFilters.currentPage;
            }
            return { ...state, planFilters: newFilters };
        }

        // ---- Approval Actions ----
        case 'PIP_SET_APPROVAL_LOADING': {
            return { ...state, approvalLoading: action.payload };
        }
        case 'PIP_SET_APPROVAL_LIST': {
            return {
                ...state,
                approvals: action.payload?.results || action.payload?.rows || [],
                approvalTotalRecords: action.payload?.totalRecords || 0,
                approvalLoading: false,
            };
        }
        case 'PIP_SET_APPROVAL_FILTERS': {
            const newFilters = { ...state.approvalFilters, ...action.payload };
            if (action.payload?.currentPage === undefined && (
                action.payload?.approvalStatus !== undefined ||
                action.payload?.search !== undefined
            )) {
                newFilters.currentPage = initialPipState.approvalFilters.currentPage;
            }
            return { ...state, approvalFilters: newFilters };
        }

        // ---- Task Approval Actions ----
        case 'PIP_SET_TASK_APPROVAL_FILTERS': {
            const newFilters = { ...state.taskApprovalFilters, ...action.payload };
            if (action.payload?.currentPage === undefined && (
                action.payload?.approvalStatus !== undefined ||
                action.payload?.user !== undefined
            )) {
                newFilters.currentPage = initialPipState.taskApprovalFilters.currentPage;
            }
            return { ...state, taskApprovalFilters: newFilters };
        }

        // ---- Closed Plan Actions ----
        case 'PIP_SET_CLOSED_LOADING': {
            return { ...state, closedLoading: action.payload };
        }
        case 'PIP_SET_CLOSED_LIST': {
            return {
                ...state,
                closedPlans: action.payload?.results || action.payload?.rows || [],
                closedTotalRecords: action.payload?.totalRecords || 0,
                closedLoading: false,
            };
        }
        case 'PIP_SET_CLOSED_FILTERS': {
            const newFilters = { ...state.closedFilters, ...action.payload };
            if (action.payload?.currentPage === undefined && (
                action.payload?.department !== undefined ||
                action.payload?.decision !== undefined
            )) {
                newFilters.currentPage = initialPipState.closedFilters.currentPage;
            }
            return { ...state, closedFilters: newFilters };
        }

        // ---- My PIP Actions ----
        case 'PIP_SET_MY_PIP_LOADING': {
            return { ...state, myPipLoading: action.payload };
        }
        case 'PIP_SET_MY_PIP_LIST': {
            return {
                ...state,
                myPips: action.payload?.results || action.payload?.rows || [],
                myPipTotalRecords: action.payload?.totalRecords || 0,
                myPipLoading: false,
            };
        }
        case 'PIP_SET_MY_PIP_FILTERS': {
            const newFilters = { ...state.myPipFilters, ...action.payload };
            if (action.payload?.currentPage === undefined && (
                action.payload?.search !== undefined ||
                action.payload?.status !== undefined ||
                action.payload?.pipId !== undefined ||
                action.payload?.startDate !== undefined ||
                action.payload?.endDate !== undefined
            )) {
                newFilters.currentPage = initialPipState.myPipFilters.currentPage;
            }
            return { ...state, myPipFilters: newFilters };
        }

        // ---- Global ----
        case 'PIP_CLEAR': {
            return { ...initialPipState };
        }
        default:
            return state;
    }
};
